import { Button, Card, Center, Loader, Stack, Table, Tabs, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPackage, IconPlus } from '@tabler/icons-react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { PageTitle } from 'components/PageTitle';
import { usePagination } from 'hooks/use-pagination';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { ProductFormModal } from 'shared/forms/ProductForm/ProductFormModal';
import {
  type LightProduct,
  type ProductFilters,
  type ProductVariables,
  type UpdateProductVariables,
  useDeleteProduct,
  useLoadProducts,
  useSaveProduct,
  useUpdateProduct,
} from '../api';
import { useProductColumns } from './getProductColumns';

export default function ProductsIndex() {
  const { t } = useTranslation();
  const { currentPage, pageLimit, resetPagination, PaginationFooter } = usePagination({
    indexPath: 'products',
  });

  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const isArchivedTab = activeTab === 'archived';

  const params: ProductFilters = {
    page: currentPage,
    pageLimit: pageLimit,
    ...(isArchivedTab ? { isArchived: true } : {}),
  };

  const { products, isProductsLoading, isProductsFetching } = useLoadProducts(params);
  const { saveProduct } = useSaveProduct();
  const { updateProduct } = useUpdateProduct();
  const { deleteProduct } = useDeleteProduct();

  const displayedProducts = products.data;

  const handleCreate = (values: ProductVariables) => {
    saveProduct(values, {
      onSuccess: () => modals.closeAll(),
    });
  };

  const handleNewProduct = () => {
    modals.open({
      size: 'md',
      title: t(i18nKeys.NEW_PRODUCT),
      centered: true,
      children: <ProductFormModal onSubmit={handleCreate} />,
    });
  };

  const handleUpdate = (values: UpdateProductVariables) => {
    updateProduct(values, {
      onSuccess: () => modals.closeAll(),
    });
  };

  const handleDelete = (product: LightProduct) => {
    modals.openConfirmModal({
      title: t(i18nKeys.CONFIRMATION),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.INVOICING.PRODUCT.AYS_TO_DELETE)}</Text>,
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteProduct({ id: product.id }),
    });
  };

  const handleToggleArchive = (product: LightProduct) => {
    updateProduct({ id: product.id, archived: !product.isArchived });
  };

  const handleEdit = (product: LightProduct) => {
    modals.open({
      size: 'md',
      title: t(i18nKeys.FORM.PRODUCT.EDIT),
      centered: true,
      children: <ProductFormModal onSubmit={handleUpdate} initialValues={product} />,
    });
  };

  const columns = useProductColumns({
    onDelete: handleDelete,
    onToggleArchive: handleToggleArchive,
  });

  const table = useReactTable({
    data: displayedProducts,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const hasNoProducts = displayedProducts.length === 0 && !isProductsFetching;

  if (isProductsLoading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack mih="100%" gap={0}>
      <PageTitle>
        {t(i18nKeys.INVOICING.PRODUCT.TITLE)}
        <PageTitle.Actions>
          <Button variant="filled" leftSection={<IconPlus size={16} />} onClick={handleNewProduct}>
            {t(i18nKeys.NEW_PRODUCT)}
          </Button>
        </PageTitle.Actions>
      </PageTitle>
      <Tabs
        value={activeTab}
        onChange={value => {
          setActiveTab((value as 'active' | 'archived') ?? 'active');
          resetPagination();
        }}
        mb="md"
      >
        <Tabs.List>
          <Tabs.Tab value="active">{t(i18nKeys.INVOICING.PRODUCT.TAB_ACTIVE)}</Tabs.Tab>
          <Tabs.Tab value="archived">{t(i18nKeys.INVOICING.PRODUCT.TAB_ARCHIVED)}</Tabs.Tab>
        </Tabs.List>
      </Tabs>
      <Card radius={8} p={0}>
        <Table>
          <Table.Thead>
            {table.getHeaderGroups().map(headerGroup => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Table.Th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {table.getRowModel().rows.map(row => (
              <Table.Tr
                key={row.id}
                onClick={() => handleEdit(row.original)}
                style={{ cursor: 'pointer' }}
              >
                {row.getVisibleCells().map(cell => (
                  <Table.Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {hasNoProducts && <EmptyProductsState isArchivedTab={isArchivedTab} />}
        <PaginationFooter total={products.pagination.totalItems} />
      </Card>
    </Stack>
  );
}

const EmptyProductsState = ({ isArchivedTab }: { isArchivedTab: boolean }) => {
  const { t } = useTranslation();

  return (
    <Center my="md" w="100%">
      <Stack align="center">
        <IconPackage size={75} stroke={1.5} color="gray" />
        <Text size="sm">
          {isArchivedTab
            ? t(i18nKeys.INVOICING.PRODUCT.EMPTY_ARCHIVED)
            : t(i18nKeys.INVOICING.PRODUCT.EMPTY)}
        </Text>
      </Stack>
    </Center>
  );
};
